/**
 * Mobile viewport utilities
 *
 * Handles viewport reset after modal dialogs close on mobile devices.
 * Radix UI dialogs use react-remove-scroll which can leave the visual viewport
 * in an offset state, causing fixed-positioned elements (like the nav bar)
 * to appear shifted off-screen when the user interacts with the page.
 */
/**
 * Reset the visual viewport to remove any offset caused by modal dialogs.
 *
 * On mobile browsers, dialogs that manipulate scroll behaviour can leave
 * the visual viewport in an offset/scaled state. This causes fixed-positioned
 * elements (like the nav bar at `position: fixed; top: 0;`) to appear
 * shifted off-screen when the user touches the screen to drag/scroll.
 *
 * Call this function when closing modal dialogs on mobile.
 */
export function resetVisualViewport() {
    // Method 1: Use the Visual Viewport API if available
    if (window.visualViewport) {
        const vv = window.visualViewport;
        // If the visual viewport is offset or scaled, scroll to reset it
        if (vv.offsetTop !== 0 || vv.offsetLeft !== 0 || vv.scale !== 1) {
            window.scrollTo(0, 0);
        }
    }
    // Method 2: Force a scroll reset regardless (fallback for older browsers)
    // This ensures the document is scrolled to top-left
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    // Method 3: Reset document scroll position on both html and body
    // Different browsers store scroll position in different places
    document.documentElement.scrollTop = 0;
    document.documentElement.scrollLeft = 0;
    document.body.scrollTop = 0;
    document.body.scrollLeft = 0;
}
/**
 * Creates a close handler that resets the viewport after the dialog closes.
 * Use this to wrap onClose handlers for Radix dialogs on mobile.
 *
 * @param onClose - The original close handler
 * @param delay - Delay in ms before resetting viewport (default: 50)
 * @returns Wrapped close handler
 */
export function createMobileDialogCloseHandler(onClose, delay = 50) {
    return () => {
        onClose();
        // Small delay to ensure dialog has unmounted before resetting viewport
        setTimeout(() => {
            resetVisualViewport();
        }, delay);
    };
}
